import axios from 'axios';
import { API_CONFIG } from '../config';
import { auth } from '../firebase';
import { UserProfilePayload, User } from '../types/user';
import { Coordinates } from '../types/event';

/**
 * Définition explicite du type pour ENDPOINTS pour éviter les erreurs TypeScript
 */
interface ApiEndpoints {
  EVENTS: string;
  ORGANIZATIONS: string;
  USERS: string;
  STAFF: string;
}

// Assertion de type pour garantir que TypeScript reconnaît la propriété USERS
const ENDPOINTS = API_CONFIG.ENDPOINTS as ApiEndpoints;

// Configuration de l'instance Axios
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important pour les cookies
});

// Variables pour gérer les limites de rafraîchissement de token
let tokenRefreshAttempts = 0;
const MAX_TOKEN_REFRESH_ATTEMPTS = 3;
let lastTokenRefresh = 0;
const MIN_TOKEN_REFRESH_INTERVAL = 60000; // 1 minute minimum entre les tentatives
let tokenRefreshPromise: Promise<string> | null = null;
let isRefreshingToken = false;

// Fonction pour obtenir un token Firebase avec gestion des limites de quota
export const getFirebaseToken = async (forceRefresh = false): Promise<string> => {
  try {
    // Si une demande est déjà en cours, retourner la promesse existante
    if (isRefreshingToken && tokenRefreshPromise) {
      console.log('Requête en attente d\'un token déjà en cours d\'acquisition...');
      return tokenRefreshPromise as Promise<string>;
    }

    // Vérifier si nous avons dépassé les tentatives maximales
    if (tokenRefreshAttempts >= MAX_TOKEN_REFRESH_ATTEMPTS) {
      const timeSinceLastRefresh = Date.now() - lastTokenRefresh;
      
      // Si le délai minimum n'est pas écoulé, rejeter la demande
      if (timeSinceLastRefresh < MIN_TOKEN_REFRESH_INTERVAL) {
        const waitTime = Math.ceil((MIN_TOKEN_REFRESH_INTERVAL - timeSinceLastRefresh) / 1000);
        console.warn(`Trop de tentatives de rafraîchissement de token. Veuillez réessayer dans ${waitTime} secondes.`);
        throw new Error(`Limite de quota Firebase atteinte. Veuillez réessayer plus tard (${waitTime}s).`);
      } else {
        // Réinitialiser le compteur après le délai
        tokenRefreshAttempts = 0;
      }
    }

    isRefreshingToken = true;
    const currentUser = auth.currentUser;
    if (!currentUser) {
      isRefreshingToken = false;
      throw new Error('Aucun utilisateur connecté');
    }

    const userEmail = currentUser.email ? maskEmail(currentUser.email) : 'utilisateur inconnu';
    console.log(`Demande de token unique pour ${userEmail} (forceRefresh: ${forceRefresh})`);

    tokenRefreshPromise = currentUser.getIdToken(forceRefresh)
      .then((token: string) => {
        lastTokenRefresh = Date.now();
        tokenRefreshAttempts = 0;
        isRefreshingToken = false;
        return token;
      })
      .catch((error: any) => {
        console.error('Erreur lors de l\'obtention du token Firebase:', error);
        tokenRefreshAttempts++;
        isRefreshingToken = false;
        throw error;
      });

    return tokenRefreshPromise as Promise<string>;
  } catch (error: any) {
    console.error('Erreur lors de l\'obtention du token Firebase:', error);
    isRefreshingToken = false;
    throw error;
  }
};

// Intercepteurs pour les requêtes
api.interceptors.request.use(async (config) => {
  try {
    console.log(`Préparation de la requête: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Déterminer si un rafraîchissement du token est nécessaire pour cette requête
    const forceRefresh = config.url?.includes('/me') || 
                         config.url?.includes('/events') || 
                         config.url?.includes('/profile');
    
    // Obtenir le token avec le système de verrouillage (évite les demandes parallèles)
    const token = await getFirebaseToken(forceRefresh);
    
    // Ajouter le token à la requête s'il existe
    if (token) {
      // Utiliser le vrai token dans l'en-tête, mais l'afficher masqué dans les logs
      config.headers['Authorization'] = `Bearer ${token}`;
      
      // Pour le log, on masque le token
      console.log(`En-tête d'autorisation ajouté pour ${config.url} (token masqué)`);
      
      // Désactiver le cache pour les requêtes importantes
      if (config.url?.includes('/me') || config.url?.includes('/events') || config.url?.includes('/profile')) {
        // Ajouter des headers pour éviter la mise en cache
        config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        config.headers['Pragma'] = 'no-cache';
        config.headers['Expires'] = '0';
        
        // Ajouter un timestamp pour forcer une nouvelle requête
        const separator = config.url.includes('?') ? '&' : '?';
        config.url = `${config.url}${separator}_t=${Date.now()}`;
        
        console.log(`En-tête d'autorisation ajouté pour ${config.url} avec cache désactivé`);
      }
      
      // S'assurer que le token est aussi défini dans axios.defaults pour les appels directs
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn(`Aucun token Firebase disponible pour la requête API: ${config.url}`);
      
      // Dernière tentative: essayer de récupérer un token depuis axios.defaults
      const authHeader = axios.defaults.headers.common['Authorization'];
      if (authHeader && typeof authHeader === 'string' && authHeader.startsWith('Bearer ')) {
        // Récupérer et utiliser le vrai token, pas un placeholder
        const defaultToken = authHeader.substring(7); // Extraire le token sans "Bearer "
        config.headers['Authorization'] = `Bearer ${defaultToken}`;
        console.log('Token récupéré depuis axios.defaults (dernier recours)');
      }
    }
    
    return config;
  } catch (error: any) {
    console.error('Erreur dans l\'intercepteur de requête:', error);
    return config;
  }
}, (error: any) => {
  return Promise.reject(error);
});

// Intercepteur pour les réponses
api.interceptors.response.use(
  (response) => response,
  async (error: any) => {
    const originalRequest = error.config;

    // Éviter les boucles infinies
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      console.log('Erreur 401 - Tentative de rafraîchissement du token Firebase...');
      
      // Vérifier si nous avons dépassé les tentatives maximales
      if (tokenRefreshAttempts >= MAX_TOKEN_REFRESH_ATTEMPTS) {
        const timeSinceLastRefresh = Date.now() - lastTokenRefresh;
        
        // Si le délai minimum n'est pas écoulé, rejeter la demande
        if (timeSinceLastRefresh < MIN_TOKEN_REFRESH_INTERVAL) {
          console.warn('Trop de tentatives de rafraîchissement. Attente imposée avant nouvel essai.');
          return Promise.reject(new Error('Limite de quota Firebase atteinte. Veuillez vous reconnecter.'));
        }
      }

      try {
        originalRequest._retry = true;
        
        // Tenter de rafraîchir le token
        const token = await getFirebaseToken(true);
        console.log('Token Firebase rafraîchi suite à erreur 401');
        
        // Mettre à jour le token dans la requête originale et les en-têtes par défaut
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        
        // Réessayer la requête originale
        return api(originalRequest);
      } catch (refreshError: any) {
        console.error('Échec du rafraîchissement du token Firebase:', refreshError);
        
        // En cas d'échec, nettoyer les données d'authentification
        userService.cleanupAuthData(true);
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Récupérer l'ID utilisateur du sessionStorage, si disponible
const getUserId = (): number | null => {
  const userData = sessionStorage.getItem('userData');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      return user.userId !== undefined ? user.userId : null;
    } catch (e) {
      console.error('Erreur lors de la récupération des données utilisateur:', e);
      return null;
    }
  }
  return null;
};

interface CreateEventPayload {
  title: string;
  description?: string;
  dateTime: string;
  location: string;
  category: string;
  subcategory?: string;
  tags?: string;
  maxParticipants?: number;
  imageUrl?: string;
  organizationId: number;
  coordinates?: Coordinates;
  latitude?: number;
  longitude?: number;
}

interface OrganizationPayload {
  name: string;
  description: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  logoUrl: string;
  isActive: boolean;
  coordinates?: Coordinates;
  latitude?: number;
  longitude?: number;
}

// Helper function to ensure coordinates always have both lon and lng properties
const normalizeCoordinates = (coords: Coordinates): Coordinates => {
  if (!coords) return coords;
  
  return {
    lat: coords.lat,
    lon: coords.lon || (coords.lng as number),
    lng: coords.lng || coords.lon
  };
};

export const eventService = {
  // Obtenir tous les événements avec possibilité de filtres
  getAll: async (params?: {
    category?: string;
    search?: string;
    upcoming?: boolean;
    userLocation?: Coordinates;
    maxDistance?: number;
    sortByDistance?: boolean;
  }) => {
    try {
      // Construire les paramètres de requête
      const queryParams: any = {};
      
      if (params?.category) queryParams.category = params.category;
      if (params?.search) queryParams.search = params.search;
      if (params?.upcoming !== undefined) queryParams.upcoming = params.upcoming;
      
      // Récupérer les événements depuis l'API
      const { data } = await api.get(ENDPOINTS.EVENTS, { params: queryParams });
      
      // Transformer la réponse
      let events = data.map((event: any) => {
        // Mapper les données de l'événement pour le frontend
        const mappedEvent = {
          ...event,
          interested: event.interestedUsers?.some((u: any) => u.id === getUserId()) || false,
          saved: event.savedByUsers?.some((u: any) => u.id === getUserId()) || false,
          // Ajouter d'autres propriétés utiles au frontend
        };
        
        // Si l'événement n'a pas d'objet coordinates mais a latitude/longitude, créer l'objet
        if (!event.coordinates && event.latitude !== undefined && event.longitude !== undefined) {
          mappedEvent.coordinates = {
            lat: Number(event.latitude),
            lon: Number(event.longitude),
            lng: Number(event.longitude)
          };
        }
        
        return mappedEvent;
      });
      
      // Ajouter des données de distance si la localisation de l'utilisateur est fournie
      if (params?.userLocation && params.userLocation.lat && (params.userLocation.lon || params.userLocation.lng)) {
        const userLocation = normalizeCoordinates(params.userLocation);
        console.log('Distance calculation enabled with user location:', userLocation);
        
        // Calculer la distance pour chaque événement et l'ajouter au résultat
        events = events.map((event: any) => {
          if (event.coordinates) {
            const eventCoords = normalizeCoordinates(event.coordinates);
            const distance = calculateDistance(userLocation, eventCoords);
            return { ...event, distance };
          } else if (event.latitude !== undefined && event.longitude !== undefined) {
            // Convertir les propriétés latitude/longitude en objet coordinates compatible
            const eventCoords: Coordinates = {
              lat: parseFloat(event.latitude),
              lon: parseFloat(event.longitude),
              lng: parseFloat(event.longitude)
            };
            // Vérifier que les coordonnées sont des nombres valides
            if (!isNaN(eventCoords.lat) && !isNaN(eventCoords.lon)) {
              const distance = calculateDistance(userLocation, eventCoords);
              return { ...event, distance };
            }
          }
          return event;
        });
        
        // Filtrer par distance maximale si spécifiée
        if (params.maxDistance && params.maxDistance > 0) {
          const maxDistance = params.maxDistance; // Create a local constant to avoid the undefined error
          events = events.filter((event: any) => 
            event.distance !== undefined && event.distance <= maxDistance
          );
        }
        
        // Trier par distance si demandé
        if (params.sortByDistance) {
          events = events.sort((a: any, b: any) => {
            // Si les deux événements ont une distance calculée, les comparer
            if (a.distance !== undefined && b.distance !== undefined) {
              return a.distance - b.distance;
            }
            // Sinon, les événements sans distance vont à la fin
            if (a.distance === undefined) return 1;
            if (b.distance === undefined) return -1;
            return 0;
          });
        }
      }
      
      // Normaliser les compteurs
      events = events.map((event: any) => {
        const interestCount = event._count?.interests || event.interestedCount || 0;
        const savedCount = event._count?.savedBy || event.savedByCount || 0;
        const viewCount = event._count?.views || event.viewCount || (Array.isArray(event.views) ? event.views.length : (typeof event.views === 'number' ? event.views : 0));
        
        return {
          ...event,
          _count: {
            ...(event._count || {}),
            interests: interestCount,
            savedBy: savedCount,
            views: viewCount
          },
          interestedCount: interestCount,
          savedByCount: savedCount,
          viewCount: viewCount
        };
      });
      
      return events;
    } catch (err: any) {
      console.error('Erreur lors de la récupération des événements:', err);
      return [];
    }
  },

  // Obtenir les événements de l'utilisateur
  getUserEvents: async (): Promise<any[]> => {
    const userId = getUserId();
    if (!userId) {
      console.warn('Utilisateur non connecté, impossible de récupérer les événements');
      return [];
    }
    
    try {
      // Ajouter des paramètres pour inclure les compteurs d'intérêts, sauvegardes et vues
      const { data } = await api.get(`${ENDPOINTS.USERS}/${userId}/events`, {
        params: {
          includeCounts: true,
          includeInterests: true, 
          includeSaves: true,
          includeViews: true
        }
      });
      
      console.log('Données des événements reçues de l\'API:', data);
      
      // Transformer les événements pour s'assurer que les compteurs sont présents
      return data.map((event: any) => {
        // S'assurer que la structure _count existe
        if (!event._count) {
          event._count = {};
        }
        
        // Pour le débogage - afficher les propriétés de comptage disponibles
        console.log(`Événement ${event.id}: propriétés de comptage disponibles:`, {
          interestedCount: event.interestedCount,
          isInterested: event.isInterested,
          savedCount: event.savedCount,
          isSaved: event.isSaved,
          viewCount: event.viewCount,
          _count: event._count
        });
        
        // Synchroniser les compteurs à partir des propriétés individuelles vers _count
        // Pour les intérêts, utiliser interestedCount s'il existe
        event._count.interests = event.interestedCount || 
                               (event._count && event._count.interests) || 
                               (Array.isArray(event.interestedUsers) ? event.interestedUsers.length : 0);
        
        // Pour les sauvegardes, utiliser savedByCount en priorité, puis savedCount si disponible
        event._count.savedBy = event.savedByCount || 
                             event.savedCount || 
                             (event._count && event._count.savedBy) || 
                             (Array.isArray(event.savedByUsers) ? event.savedByUsers.length : 0);
        
        // Pour les vues, utiliser viewCount s'il existe
        event._count.views = event.viewCount || 
                           (event._count && event._count.views) || 
                           0;
        
        console.log(`Événement ${event.id}: _count après synchronisation:`, event._count);
        
        return {
          ...event,
          interested: event.isInterested || event.interestedUsers?.some((u: any) => u.id === userId) || false,
          saved: event.isSaved || event.savedByUsers?.some((u: any) => u.id === userId) || false
        };
      });
    } catch (err: any) {
      console.error('Erreur lors de la récupération des événements de l\'utilisateur:', err);
      return [];
    }
  },

  // Obtenir un événement par son ID
  getById: async (id: number): Promise<any> => {
    // Vérifier que l'ID est un nombre valide
    if (isNaN(id)) {
      throw new Error('ID d\'événement invalide');
    }
    
    const { data } = await api.get(`${ENDPOINTS.EVENTS}/${id}`);
    const userId = getUserId();
    
    // Normaliser les compteurs
    const interestCount = data._count?.interests || data.interestedCount || 0;
    const savedCount = data._count?.savedBy || data.savedByCount || 0;
    const viewCount = data._count?.views || data.viewCount || (Array.isArray(data.views) ? data.views.length : (typeof data.views === 'number' ? data.views : 0));
    
    return {
      ...data,
      interested: data.interestedUsers?.some((u: any) => u.id === userId) || false,
      saved: data.savedByUsers?.some((u: any) => u.id === userId) || false,
      _count: {
        ...(data._count || {}),
        interests: interestCount,
        savedBy: savedCount,
        views: viewCount
      },
      interestedCount: interestCount,
      savedByCount: savedCount,
      viewCount: viewCount
    };
  },

  // Créer un événement
  create: async (event: CreateEventPayload): Promise<any> => {
    try {
      // Copy event data to avoid modifying the original object
      let eventData = { ...event };
      
      // Convert coordinates to latitude/longitude format
      if (eventData.coordinates) {
        eventData = {
          ...eventData,
          latitude: eventData.coordinates.lat,
          longitude: eventData.coordinates.lng || eventData.coordinates.lon,
          coordinates: undefined // Remove coordinates object as backend doesn't expect it
        };
      }
      
      // If no coordinates but has a location, try to get coordinates
      if (!eventData.coordinates && eventData.location) {
        console.log('Tentative de géocodage de l\'adresse:', eventData.location);
        const coordinates = await geocodeAddress(eventData.location);
        if (coordinates) {
          eventData = {
            ...eventData,
            latitude: coordinates.lat,
            longitude: coordinates.lng || coordinates.lon
          };
          console.log('Coordonnées obtenues par géocodage:', coordinates);
        } else {
          console.warn('Impossible d\'obtenir les coordonnées pour l\'adresse:', eventData.location);
        }
      }
      
      console.log('Sending event data to API:', eventData);
      const { data } = await api.post(ENDPOINTS.EVENTS, eventData);
      return data;
    } catch (err: any) {
      console.error('Erreur lors de la création de l\'événement:', err);
      throw err;
    }
  },

  // Activer/désactiver l'intérêt pour un événement
  toggleInterest: async (eventId: number): Promise<void> => {
    const userId = getUserId();
    if (!userId) throw new Error('Vous devez être connecté pour effectuer cette action');
    
    await api.post(`${ENDPOINTS.EVENTS}/${eventId}/interest`, { userId });
  },

  // Activer/désactiver la sauvegarde d'un événement
  toggleSave: async (eventId: number): Promise<void> => {
    const userId = getUserId();
    if (!userId) throw new Error('Vous devez être connecté pour effectuer cette action');
    
    await api.post(`${ENDPOINTS.EVENTS}/${eventId}/save`, { userId });
  },

  // Enregistrer une vue pour un événement
  recordView: async (eventId: number): Promise<{ viewCount: number }> => {
    try {
      const userId = getUserId();
      const { data } = await api.post(`${ENDPOINTS.EVENTS}/${eventId}/view`, { userId });
      
      // Mettre à jour le cache local si nécessaire
      try {
        // Tenter de récupérer les événements en cache pour mettre à jour le compteur de vues
        const cachedEventsString = sessionStorage.getItem('cachedEvents');
        if (cachedEventsString) {
          const cachedEvents = JSON.parse(cachedEventsString);
          
          // Mettre à jour le compteur pour l'événement spécifique
          const updatedEvents = cachedEvents.map((event: any) => {
            if (event.id === eventId) {
              return {
                ...event,
                _count: {
                  ...(event._count || {}),
                  views: data.viewCount
                },
                viewCount: data.viewCount
              };
            }
            return event;
          });
          
          // Sauvegarder les événements mis à jour
          sessionStorage.setItem('cachedEvents', JSON.stringify(updatedEvents));
        }
      } catch (cacheError) {
        console.warn('Erreur lors de la mise à jour du cache des événements:', cacheError);
      }
      
      return data;
    } catch (error) {
      console.error(`Erreur lors de l'enregistrement de la vue pour l'événement ${eventId}:`, error);
      throw error;
    }
  },

  // Obtenir les statistiques de vues pour un événement
  getViewStats: async (eventId: number): Promise<any> => {
    const { data } = await api.get(`${ENDPOINTS.EVENTS}/${eventId}/views/stats`);
    return data;
  },
  
  // Obtenir les événements à proximité
  getNearbyEvents: async (userLocation: Coordinates, maxDistance: number = 10): Promise<any[]> => {
    try {
      return await eventService.getAll({
        userLocation,
        maxDistance,
        sortByDistance: true
      });
    } catch (err: any) {
      console.error('Erreur lors de la récupération des événements à proximité:', err);
      return [];
    }
  },
  
  // Calculer la distance entre l'utilisateur et un événement
  calculateDistanceToEvent: async (eventId: number, userLocation: Coordinates): Promise<number | null> => {
    try {
      const normalizedLocation = normalizeCoordinates(userLocation);
      const event = await eventService.getById(eventId);
      
      if (!event) {
        console.log(`Événement ${eventId} introuvable`);
        return null;
      }
      
      if (event.coordinates) {
        return calculateDistance(normalizedLocation, normalizeCoordinates(event.coordinates));
      } else if (event.latitude !== undefined && event.longitude !== undefined) {
        // Convertir les propriétés latitude/longitude en objet coordinates compatible
        const eventCoords: Coordinates = {
          lat: Number(event.latitude),
          lon: Number(event.longitude),
          lng: Number(event.longitude)
        };
        // Vérifier que les coordonnées sont des nombres valides
        if (!isNaN(eventCoords.lat) && !isNaN(eventCoords.lon)) {
          return calculateDistance(normalizedLocation, eventCoords);
        }
      }
      
      return null;
    } catch (err: any) {
      console.error('Erreur lors du calcul de la distance:', err);
      return null;
    }
  }
};

export const organizationService = {
  // Obtenir toutes les organisations
  getAll: async (): Promise<any[]> => {
    const { data } = await api.get(ENDPOINTS.ORGANIZATIONS);
    return data;
  },
  
  // Obtenir les organisations de l'utilisateur actuellement connecté
  getUserOrganizations: async (): Promise<any[]> => {
    // Récupérer les organisations de l'utilisateur actuellement connecté
    try {
      // Utiliser directement l'endpoint "me" pour récupérer les organisations de l'utilisateur authentifié
      // au lieu de passer par l'endpoint /users/{id}/organizations qui n'existe pas
      const { data } = await api.get(`${ENDPOINTS.ORGANIZATIONS}/me`);
      return data;
    } catch (err: any) {
      console.error('Erreur lors de la récupération des organisations:', err);
      return [];
    }
  },

  // Obtenir les organisations de l'utilisateur
  getByUserId: async (userId: number): Promise<any[]> => {
    if (!userId) {
      throw new Error('Utilisateur non connecté');
    }
    const { data } = await api.get(`${ENDPOINTS.USERS}/${userId}/organizations`);
    return data;
  },

  // Obtenir une organisation par son ID
  getById: async (id: number): Promise<any> => {
    const { data } = await api.get(`${ENDPOINTS.ORGANIZATIONS}/${id}`);
    return data;
  },
  
  // Obtenir les événements d'une organisation
  getEvents: async (id: number): Promise<any[]> => {
    const { data } = await api.get(`${ENDPOINTS.ORGANIZATIONS}/${id}/events`);
    const userId = getUserId();
    
    // Ajouter des infos utiles pour le frontend
    return data.map((event: any) => {
      // Count data normalization
      const interestCount = event._count?.interests || event.interestedCount || 0;
      const savedCount = event._count?.savedBy || event.savedByCount || 0;
      const viewCount = event._count?.views || event.viewCount || (Array.isArray(event.views) ? event.views.length : (typeof event.views === 'number' ? event.views : 0));
      
      return {
        ...event,
        isInterested: event.interestedUsers?.some((u: any) => u.id === userId) || false,
        isSaved: event.savedByUsers?.some((u: any) => u.id === userId) || false,
        // Ensure we have both formats of count data
        _count: {
          ...(event._count || {}),
          interests: interestCount,
          savedBy: savedCount,
          views: viewCount
        },
        interestedCount: interestCount,
        savedByCount: savedCount,
        viewCount: viewCount
      };
    });
  },

  // Créer une organisation
  create: async (organization: OrganizationPayload): Promise<any> => {
    // Vérifier les champs requis
    if (!organization.name || !organization.description || !organization.email) {
      throw new Error('Veuillez remplir tous les champs obligatoires');
    }
    
    try {
      // Convertir les coordonnées si elles existent
      let orgData = { ...organization };
      
      if (orgData.coordinates) {
        orgData = {
          ...orgData,
          latitude: orgData.coordinates.lat,
          longitude: orgData.coordinates.lng || orgData.coordinates.lon,
          coordinates: undefined
        };
      }
      
      const { data } = await api.post(ENDPOINTS.ORGANIZATIONS, orgData);
      return data;
    } catch (err: any) {
      console.error('Erreur lors de la création de l\'organisation:', err);
      throw err;
    }
  },

  // Mettre à jour une organisation
  update: async (id: number, organization: OrganizationPayload): Promise<any> => {
    const { data } = await api.put(`${ENDPOINTS.ORGANIZATIONS}/${id}`, organization);
    return data;
  }
};

export const userService = {
  // Récupérer l'utilisateur actuellement connecté
  getCurrentUser: async (): Promise<any> => {
    try {
      // Vérifier si nous sommes en contexte post-redirection SSO
      const isRedirectContext = sessionStorage.getItem('sso_redirect_triggered') === 'true';
      
      // Vérifier si les données utilisateur sont déjà dans le sessionStorage
      const cachedUserData = sessionStorage.getItem('userData');
      
      // En cas de redirection, ne pas utiliser le cache pour garantir des données fraîches
      if (cachedUserData && !isRedirectContext) {
        try {
          const parsedData = JSON.parse(cachedUserData);
          
          // Vérifier que les données du cache sont complètes et valides
          if (parsedData && 
              parsedData.userId && 
              parsedData.email && 
              parsedData.email.length > 0) {
            
            console.log('📦 API: Données utilisateur complètes récupérées depuis le cache');
            return parsedData;
          } else {
            console.log('⚠️ API: Données utilisateur incomplètes dans le cache, appel API nécessaire');
          }
        } catch (e) {
          console.error('❌ API: Erreur lors de la lecture des données utilisateur depuis le cache:', e);
          // Si erreur de parsing, continuer pour récupérer les données depuis l'API
        }
      } else if (isRedirectContext) {
        console.log('🔄 API: Contexte post-redirection SSO, ignorant le cache pour des données fraîches');
      }
      
      // Si pas de données en cache ou erreur, récupérer depuis l'API
      console.log(`🚀 API: Envoi requête GET vers ${ENDPOINTS.USERS}/me`);
      
      // Récupérer le token Firebase si disponible
      let authToken = '';
      if (auth?.currentUser) {
        try {
          authToken = await auth.currentUser.getIdToken();
          console.log('🔑 API: Token Firebase récupéré pour authentification');
        } catch (e) {
          console.error('❌ API: Erreur lors de la récupération du token Firebase:', e);
        }
      } else {
        console.warn('⚠️ API: Pas d\'utilisateur Firebase connecté pour la requête /users/me');
      }
      
      // Ajouter des logs sur les headers pour vérifier si le token est bien envoyé
      const headers = {
        'Authorization': authToken ? `Bearer ${authToken}` : '',
      };
      console.log('📋 API: Headers de la requête:', headers);
      
      // Envoi de la requête avec le token
      const { data } = await api.get(`${ENDPOINTS.USERS}/me`, { headers });
      console.log('✅ API: Données utilisateur récupérées depuis l\'API avec succès');
      console.log('📄 API: Données utilisateur brutes récupérées depuis l\'API:', data);
      console.log('🔍 API: Structure complète de l\'objet utilisateur:', JSON.stringify(data, null, 2));
      console.log('🧩 API: Présence du champ role:', data?.role !== undefined);
      console.log('🧩 API: Présence du champ userId:', data?.userId !== undefined);
      console.log('🧩 API: Liste des champs disponibles:', Object.keys(data || {}));
      
      if (data) {
        // Nettoyer les données utilisateur obsolètes ou incomplètes
        sessionStorage.removeItem('userData');
        
        // Stocker dans le sessionStorage pour les futures requêtes
        sessionStorage.setItem('userData', JSON.stringify(data));
        return data;
      } else {
        return null;
      }
    } catch (err: any) {
      console.error('❌ API: Erreur lors de la récupération des données utilisateur:', err);
      console.log('🔍 API: Détails de l\'erreur:', {
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data,
        message: err?.message
      });
      
      // En cas d'erreur 401 (non autorisé), nettoyer les données d'authentification
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        console.log('🔒 API: Session expirée ou utilisateur non connecté (401)');
        userService.cleanupAuthData(true);
      }
      
      return null;
    }
  },

  // Méthode pour se connecter
  login: async (credentials: { email: string; password: string }): Promise<any> => {
    try {
      const { data } = await api.post(`${ENDPOINTS.USERS}/login`, credentials);
      
      // Stocker les données utilisateur dans le sessionStorage
      if (data && data.user) {
        sessionStorage.setItem('userData', JSON.stringify(data.user));
        sessionStorage.setItem('isLoggedIn', 'true');
        
        // Stocker les autres données utiles si nécessaire
        if (data.token) {
          localStorage.setItem('accessToken', data.token);
        }
      }
      
      return data;
    } catch (err: any) {
      console.error('Erreur lors de la connexion:', err);
      throw err;
    }
  },

  // Méthode pour s'inscrire
  register: async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
  }): Promise<any> => {
    try {
      const { data } = await api.post(`${ENDPOINTS.USERS}/register`, userData);
      
      // Stocker les données utilisateur après inscription
      if (data && data.user) {
        sessionStorage.setItem('userData', JSON.stringify(data.user));
        sessionStorage.setItem('isLoggedIn', 'true');
        
        if (data.token) {
          localStorage.setItem('accessToken', data.token);
        }
      }
      
      return data;
    } catch (err: any) {
      console.error('Erreur lors de l\'inscription:', err);
      throw err;
    }
  },

  // Obtenir un utilisateur par son ID
  getById: async (id: number): Promise<any> => {
    const { data } = await api.get(`${ENDPOINTS.USERS}/${id}`);
    return data;
  },

  // Définir le type de profil pour l'utilisateur actuel
  setProfileType: async (profileType: 'user' | 'organization'): Promise<any> => {
    // Marque le choix du type de profil pour l'utilisateur actuel
    const { data } = await api.post(`${ENDPOINTS.USERS}/me/profile-type`, { profileType });
    
    // Mettre à jour les données utilisateur en cache
    if (data && data.user) {
      sessionStorage.setItem('userData', JSON.stringify(data.user));
    }
    
    return data.user;
  },

  // Mettre à jour le profil de l'utilisateur
  updateProfile: async (profile: UserProfilePayload): Promise<User> => {
    console.log("Sending profile update:", JSON.stringify(profile, null, 2));
    
    const { data } = await api.put(`${ENDPOINTS.USERS}/me/profile`, profile);
    
    // Mettre à jour les données utilisateur en cache
    if (data) {
      sessionStorage.setItem('userData', JSON.stringify(data));
    }
    
    return data;
  },

  // Mettre à jour les préférences de l'utilisateur
  updatePreferences: async (preferences: any): Promise<any> => {
    const { data } = await api.put(`${ENDPOINTS.USERS}/me/preferences`, preferences);
    
    // Mettre à jour les données utilisateur en cache
    if (data) {
      sessionStorage.setItem('userData', JSON.stringify(data));
    }
    
    return data;
  },

  // Obtenir les événements sauvegardés par l'utilisateur
  getSavedEvents: async (): Promise<any[]> => {
    const { data } = await api.get(`${ENDPOINTS.USERS}/me/saved-events`);
    return data;
  },

  // Obtenir les événements qui intéressent l'utilisateur
  getInterestedEvents: async (): Promise<any[]> => {
    const { data } = await api.get(`${ENDPOINTS.USERS}/me/interested-events`);
    return data;
  },
  
  // Stocker les informations utilisateur après inscription
  storeUserData: (userData: any): void => {
    sessionStorage.setItem('userData', JSON.stringify(userData));
  },
  
  // Déconnecter l'utilisateur
  logout: async (): Promise<void> => {
    try {
      // Appel à l'API pour supprimer le cookie côté serveur
      await api.post(`${ENDPOINTS.USERS}/logout`);
      
      // Nettoyer les données côté client
      userService.cleanupAuthData(true);
      
      console.log('Déconnexion réussie');
    } catch (err: any) {
      console.error('Erreur lors de la déconnexion:', err);
      
      // Même en cas d'erreur, on nettoie les données côté client
      userService.cleanupAuthData(true);
      throw err;
    }
  },
  
  // Nettoyer les données d'authentification
  cleanupAuthData: (fullCleanup: boolean = false) => {
    console.log('Nettoyage des données d\'authentification...');
    
    if (fullCleanup) {
      // Nettoyage complet (déconnexion volontaire ou session expirée)
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('isLoggedIn');
      
      // Supprimer les autres données stockées
      const keysToRemove = [
        'lastViewedEvents',
        'userPreferences',
        'savedSearches',
      ];
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (e) {
          console.error(`Erreur lors de la suppression de ${key}:`, e);
        }
      });
      
      console.log('Nettoyage complet des données utilisateur effectué');
    } else {
      // Nettoyage léger (seulement certaines données, par exemple lors d'un changement de rôle)
      const userData = sessionStorage.getItem('userData');
      if (userData) {
        try {
          // Conserver certaines données utilisateur mais supprimer d'autres
          const user = JSON.parse(userData);
          const lightUser = {
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName
          };
          sessionStorage.setItem('userData', JSON.stringify(lightUser));
        } catch (e) {
          console.error('Erreur lors du nettoyage léger des données utilisateur:', e);
          // En cas d'erreur, on fait un nettoyage complet
          sessionStorage.removeItem('userData');
        }
      }
      
      console.log('Nettoyage léger des données utilisateur effectué');
    }
  },
  
  // Mettre à jour la localisation de l'utilisateur
  updateUserLocation: async (coords: Coordinates): Promise<User> => {
    // Cache de la dernière position pour éviter des mises à jour redondantes
    const locationKey = 'last_location_update';
    const lastLocation = localStorage.getItem(locationKey);

    // Formater les coordonnées pour le stockage
    const locationString = `${coords.lat},${coords.lon}`;
    
    // Si les coordonnées sont identiques à la dernière mise à jour récente, 
    // éviter une requête API inutile
    if (lastLocation === locationString) {
      console.log('📍 Position déjà à jour, requête ignorée');
      // Récupérer les données utilisateur du localStorage pour conserver la cohérence
      const userData = localStorage.getItem('userData');
      if (userData) {
        return JSON.parse(userData);
      }
    }
    
    try {
      console.log('📍 Mise à jour de la position dans le profil utilisateur');
      
      // S'assurer que les coordonnées sont au format attendu par l'API
      const normalizedCoords = normalizeCoordinates(coords);
      
      const { data } = await api.patch(`${ENDPOINTS.USERS}/me/location`, normalizedCoords);
      
      // Mettre à jour le cache de position
      localStorage.setItem(locationKey, locationString);
      
      // Mettre à jour les données utilisateur en localStorage
      if (data) {
        localStorage.setItem('userData', JSON.stringify(data));
      }
      
      return data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la mise à jour de la position:', error);
      throw error;
    }
  },
  
  // Obtenir les événements à proximité de l'utilisateur
  getNearbyEvents: async (maxDistance: number = 10): Promise<any[]> => {
    try {
      // Tenter de récupérer les coordonnées de l'utilisateur depuis le cache
      let coordinates: Coordinates | null = null;
      const userData = sessionStorage.getItem('userData');
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user.latitude && user.longitude) {
            coordinates = {
              lat: user.latitude,
              lon: user.longitude,
              lng: user.longitude
            };
          }
        } catch (e) {
          console.error('Erreur lors de la récupération des coordonnées utilisateur:', e);
        }
      }
      
      if (!coordinates) return [];
      
      const normalizedCoords = normalizeCoordinates(coordinates);
      
      return api.get(`${ENDPOINTS.EVENTS}/nearby`, {
        params: {
          lat: normalizedCoords.lat,
          lon: normalizedCoords.lon,
          maxDistance
        }
      })
      .then(({ data }) => data)
      .catch((err: any) => {
        console.error('Erreur lors de la récupération des événements à proximité:', err);
        return [];
      });
    } catch (err: any) {
      console.error('Erreur lors de la récupération des événements à proximité:', err);
      return [];
    }
  },
};

// Service pour la gestion des profils de staff, demandes de staff et candidatures
export const staffService = {
  // Gestion des profils de staff
  getStaffProfile: async (userId?: number): Promise<any | null> => {
    try {
      console.log("Tentative de récupération du profil staff");
      if (userId) {
        console.log(`Récupération du profil staff pour l'ID: ${userId}`);
        const { data } = await api.get(`${ENDPOINTS.STAFF}/profiles/${userId}`);
        console.log("Profil récupéré avec succès:", data);
        return data;
      } else {
        console.log("Récupération du profil staff de l'utilisateur connecté");
        const { data } = await api.get(`${ENDPOINTS.STAFF}/profiles/me`);
        console.log("Profil récupéré avec succès:", data);
        return data;
      }
    } catch (error: any) {
      console.error("Erreur lors de la récupération du profil staff:", error);
      if ((error as Error).message.includes('non trouvé')) {
        console.log("Profil non trouvé, retour null");
        return null;
      }
      throw error;
    }
  },
  
  createStaffProfile: async (profileData: any): Promise<any> => {
    const { data } = await api.post(`${ENDPOINTS.STAFF}/profiles`, profileData);
    return data;
  },
  
  updateStaffProfile: async (profileData: any): Promise<any> => {
    const { data } = await api.put(`${ENDPOINTS.STAFF}/profiles/${profileData.userId}`, profileData);
    return data;
  },
  
  // Recherche de staff
  searchStaffProfiles: async (params: {
    staffType?: string;
    genres?: string[];
    query?: string;
  }): Promise<any[]> => {
    const { data } = await api.get(`${ENDPOINTS.STAFF}/profiles/search`, { params });
    return data;
  },
  
  // Gestion des demandes de staff pour un événement
  getStaffRequests: async (eventId: number): Promise<any[]> => {
    const { data } = await api.get(`${ENDPOINTS.STAFF}/requests/event/${eventId}`);
    return data;
  },
  
  createStaffRequest: async (requestData: any): Promise<any> => {
    const { data } = await api.post(`${ENDPOINTS.STAFF}/requests`, requestData);
    return data;
  },
  
  updateStaffRequest: async (requestData: any): Promise<any> => {
    const { data } = await api.put(`${ENDPOINTS.STAFF}/requests/${requestData.userId}`, requestData);
    return data;
  },
  
  deleteStaffRequest: async (requestId: number): Promise<void> => {
    await api.delete(`${ENDPOINTS.STAFF}/requests/${requestId}`);
  },
  
  // Gestion des candidatures de staff
  getStaffApplications: async (): Promise<any[]> => {
    const { data } = await api.get(`${ENDPOINTS.STAFF}/applications/me`);
    return data;
  },
  
  getApplicationsForRequest: async (requestId: number): Promise<any[]> => {
    const { data } = await api.get(`${ENDPOINTS.STAFF}/applications/request/${requestId}`);
    return data;
  },
  
  createStaffApplication: async (applicationData: any): Promise<any> => {
    const { data } = await api.post(`${ENDPOINTS.STAFF}/applications`, applicationData);
    return data;
  },
  
  updateApplicationStatus: async (
    applicationId: number, 
    status: 'ACCEPTED' | 'REJECTED'
  ): Promise<any> => {
    const { data } = await api.put(`${ENDPOINTS.STAFF}/applications/${applicationId}/status`, { status });
    return data;
  },
  
  // Notifications pour les demandes de staff
  getStaffRequestsNotifications: async (): Promise<any[]> => {
    const { data } = await api.get(`${ENDPOINTS.STAFF}/notifications`);
    return data;
  }
};

// Fonction pour calculer la distance entre deux points géographiques avec la formule de Haversine
const calculateDistance = (point1: Coordinates, point2: Coordinates): number => {
  // Fonction pour convertir des degrés en radians
  const deg2rad = (deg: number): number => deg * (Math.PI / 180);
  
  // Normaliser les coordonnées pour s'assurer qu'elles ont à la fois lon et lng
  const p1 = normalizeCoordinates(point1);
  const p2 = normalizeCoordinates(point2);
  
  // S'assurer que les coordonnées sont des nombres valides
  if (!p1 || !p2 || 
      typeof p1.lat !== 'number' || typeof p1.lon !== 'number' ||
      typeof p2.lat !== 'number' || typeof p2.lon !== 'number' ||
      isNaN(p1.lat) || isNaN(p1.lon) || isNaN(p2.lat) || isNaN(p2.lon)) {
    console.error('Coordonnées invalides pour le calcul de distance:', { point1, point2 });
    return Infinity; // Retourner une valeur qui sera facilement identifiable comme erreur
  }
  
  // Rayon de la Terre en km
  const earthRadius = 6371;
  
  // Différence de latitude et longitude en radians
  const dLat = deg2rad(p2.lat - p1.lat);
  const dLon = deg2rad(p2.lon - p1.lon);
  
  // Formule de Haversine
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(p1.lat)) * Math.cos(deg2rad(p2.lat)) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = earthRadius * c;
  
  return distance;
};

// Fonction pour formatter une distance pour l'affichage
export const formatDistance = (distance: number): string => {
  if (distance === undefined || distance === null || isNaN(distance)) {
    return '';
  }
  
  // Moins de 1 km: afficher en mètres
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m`;
  }
  
  // Entre 1 et 10 km: 1 décimale
  if (distance < 10) {
    return `${distance.toFixed(1)}km`;
  }
  
  // Plus de 10 km: nombre entier
  return `${Math.round(distance)}km`;
};

// Fonction pour obtenir les coordonnées géographiques pour une adresse
const geocodeAddress = async (address: string): Promise<Coordinates | null> => {
  try {
    // Vérifier si la clé API existe
    const googleMapsApiKey = process.env.VITE_GOOGLE_MAPS_API_KEY || '';
    if (!googleMapsApiKey) {
      console.error('Clé API Google Maps non définie.');
      return null;
    }
    
    // Utilisez l'API de geocodage que vous avez intégrée
    // Exemple avec l'API Google Maps Geocoding
    const { data } = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`
    );
    
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      return {
        lat: location.lat,
        lon: location.lng,
        lng: location.lng
      };
    } else {
      console.warn('Aucun résultat trouvé pour l\'adresse:', address);
      return null;
    }
  } catch (err: any) {
    console.error('Erreur lors du géocodage de l\'adresse:', address, err);
    return null;
  }
};

/**
 * Fonction pour masquer les emails dans les logs
 * @param email Email à masquer
 * @returns Email masqué au format xx***@domain.com
 */
function maskEmail(email: string | null | undefined): string {
  if (!email) return 'email-inconnu';
  const [username, domain] = email.split('@');
  if (!username || !domain) return 'email-invalide';
  return `${username.substring(0, 2)}***@${domain}`;
}
